<script setup>
  /**
   * TransformSearchResults — search-results view for the BCS Transform™ Learning Library.
   *
   * Reached from the search box on either the Learning Dashboard or a Category
   * (Module) Detail page. The originating view navigates here (virtual router route
   * `transformSearchResults`) carrying the search term + origin; this page seeds its
   * own search box with that term, runs the search immediately, and renders the
   * matching lessons grouped by category.
   *
   * Reuse-first: the search box is the shared BcsSearchDropdown, the result cards are
   * the same TransformLessonCard used across the module, and the header mirrors the
   * dashboard exactly. Results are NOT a flat list — they are grouped by module in the
   * same order (and lesson chronology) the module and its cards appear elsewhere.
   *
   * @prop - none (uses inject() for `customNavigation` and `toggleLoadingWithMessage`)
   */
  import { inject, computed, onMounted, ref, watch } from 'vue'
  import Footer from '../FooterSection.vue'
  import Breadcrumbs from '../BreadCrumbs.vue'
  import TransformOwnerBanner from './TransformOwnerBanner.vue'
  import BcsDynamicText from '../../components/BcsDynamicText.vue'
  import BcsSearchDropdown from '../BcsSearchDropdown.vue'
  import TransformLessonCard from './TransformLessonCard.vue'
  import TransformErrorState from './TransformErrorState.vue'
  import emptySearchIcon from '@/assets/images/empty-search-icon.svg'
  import { useTransformLearningStore } from '../../stores/TransformLearningStore'
  import { useTransformSearch } from '../../composables/useTransformSearch'
  import { useLessonThumbnails } from '../../composables/useLessonThumbnails'
  import { useTransformAccessGate } from '../../composables/useTransformAccessGate'

  const customNavigation = inject('customNavigation')
  const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})

  const store = useTransformLearningStore()
  const { searchableLessons } = useTransformSearch()
  const { thumbnailUrls, resolveThumbnails } = useLessonThumbnails('TransformSearchResults')
  const { isAccessChecked, ensureAccess } = useTransformAccessGate()

  // Search term + origin come from the navigate() params set by the entry point.
  const seededTerm = computed(() => customNavigation.value.navigationParams.searchTerm ?? '')
  const originPage = computed(
    () => customNavigation.value.navigationParams.searchOrigin
      ?? customNavigation.value.routes.transformLearningDashboard.name
  )
  const originModuleId = computed(() => customNavigation.value.navigationParams.searchOriginModuleId ?? null)
  const originModuleTitle = computed(() => customNavigation.value.navigationParams.searchOriginModuleTitle ?? null)

  // The term actually reflected in the summary row ("N Results for …") — starts as the
  // seeded term and updates whenever an in-page search runs.
  const currentTerm = ref('')
  // Flat list of matched enriched lessons (the BcsSearchDropdown @search payload).
  const matchedLessons = ref([])
  // Whether a search has been performed yet (drives the empty-state vs. nothing).
  const hasSearched = ref(false)

  const sortedModules = computed(
    () => [...store.modules].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  )

  // Matched lessons grouped by module, preserving module order and lesson chronology.
  // Modules with zero matches are omitted. lessonIndexMap / videoIdsWithQuiz are derived
  // from the FULL module (not just matches) so locked-quiz "Go to Lesson N" links and the
  // "no associated quiz" note stay correct even when the referenced lesson isn't a match.
  const groupedResults = computed(() => {
    const byModule = new Map()
    for (const lesson of matchedLessons.value) {
      if (!byModule.has(lesson.moduleId)) byModule.set(lesson.moduleId, [])
      byModule.get(lesson.moduleId).push(lesson)
    }

    const groups = []
    for (const mod of sortedModules.value) {
      const lessons = byModule.get(mod.id)
      if (!lessons || !lessons.length) continue

      const detail = store.categoryDetail(mod.id)
      const lessonIndexMap = {}
      const videoIdsWithQuiz = new Set()
      for (const fl of detail?.lessons ?? []) {
        lessonIndexMap[fl.id] = fl.displayNumber ?? null
        if (fl.lessonType === 'quiz' && fl.quiz?.requiredLessonId) {
          videoIdsWithQuiz.add(fl.quiz.requiredLessonId)
        }
      }

      groups.push({ module: mod, lessons, count: lessons.length, lessonIndexMap, videoIdsWithQuiz })
    }
    return groups
  })

  const totalResults = computed(() => matchedLessons.value.length)

  function onSearch({ term, results }) {
    currentTerm.value = term
    matchedLessons.value = results
    hasSearched.value = true

    // Persist the new term (keeping the origin) so a full-page refresh restores the
    // results the user is actually looking at, not the term the page was opened with.
    // Same-page navigation updates params + persisted events without touching the back
    // stack; the guard avoids re-navigating for the term the page is already showing
    // (e.g. the auto-search the seeded dropdown fires on mount).
    const t = (term ?? '').trim()
    if (t && t !== seededTerm.value) {
      customNavigation.value.navigate({
        newPage: customNavigation.value.routes.transformSearchResults.name,
        params: {
          searchTerm: t,
          searchOrigin: originPage.value,
          searchOriginModuleId: originModuleId.value ?? undefined,
          searchOriginModuleTitle: originModuleTitle.value ?? undefined,
        },
      })
    }
  }

  // Both the in-input clear (×) and the "Clear Search" link return the user to wherever
  // the search started — the Learning Dashboard or the originating Category Detail page.
  function clearAndReturn() {
    const nav = customNavigation.value
    if (originPage.value === nav.routes.transformModuleDetail.name && originModuleId.value) {
      nav.navigate({
        newPage: nav.routes.transformModuleDetail.name,
        params: { moduleId: originModuleId.value, moduleTitle: originModuleTitle.value ?? '' },
      })
      return
    }
    nav.navigate({ newPage: nav.routes.transformLearningDashboard.name })
  }

  // Resolve Storage thumbnail URLs for matched lessons as results change. The composable
  // filters lessons without a thumbnailFileName and dedups internally.
  watch(matchedLessons, (lessons) => resolveThumbnails(lessons))

  async function loadSearchData() {
    currentTerm.value = seededTerm.value
    toggleLoadingWithMessage(true, null)
    try {
      await store.loadDashboard()
      await store.refreshProgress()
      // Quiz state (locked/questions/passed) for every module must be loaded before the
      // full catalogue is searchable — searches span all modules, not just one.
      await Promise.all(store.modules.map((m) => store.loadCategoryDetail(m.id)))
      isAccessChecked.value = true
    } finally {
      toggleLoadingWithMessage(false, null)
    }
  }

  onMounted(async () => {
    if (!(await ensureAccess())) return
    await loadSearchData()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
</script>

<template>
  <div v-if="isAccessChecked && store.error" class="transform-search-results bcs-page-padding mt-4">
    <Breadcrumbs />
    <TransformErrorState :message="store.error" @retry="loadSearchData" />
    <Footer></Footer>
  </div>
  <div v-else-if="isAccessChecked" class="transform-search-results">
    <TransformOwnerBanner />
    <div class="mt-4 bcs-page-padding">
      <Breadcrumbs />

      <!-- Header mirrors the Learning Dashboard: title + subtitle -->
      <h1 id="transformSearchResultsTitle" class="text-28 tsr-title">
        <med>BCS Transform™ Learning Dashboard</med>
      </h1>
      <p class="tsr-subtitle mb-0">
        <BcsDynamicText text="bcstLearningDashboardSubtitleDefinition" />
      </p>

      <!-- Search box (seeded with the incoming term; runs on mount) -->
      <div class="tsr-search">
            <BcsSearchDropdown
              id="transformSearchResultsSearch"
              :key="seededTerm"
              placeholder="Search lessons, quizzes, or topics"
              searchButtonText="Search"
              :items="searchableLessons"
              :searchFields="['displayTitle', 'description']"
              :arrayTerm="'displayTitle'"
              :maxSuggestions="10"
              :showResults="false"
              :initialTerm="seededTerm"
              @search="onSearch"
              @select="() => {}"
              @clear="clearAndReturn" />
          </div>

          <!-- Summary row: "N Results for X" + Clear Search (20px apart) -->
          <div v-if="hasSearched" class="tsr-summary d-flex align-items-center">
            <h2 id="searchResultsCount" class="tsr-summary-count mb-0">
              {{ totalResults }} {{ totalResults === 1 ? 'Result' : 'Results' }} for "{{ currentTerm }}"
            </h2>
            <button
              id="clearSearchBtn"
              type="button"
              class="btn btn-link p-0 tsr-clear-link"
              @click="clearAndReturn">
              Clear Search
            </button>
          </div>

          <!-- Grouped results -->
          <template v-if="groupedResults.length">
            <section
              v-for="group in groupedResults"
              :key="group.module.id"
              class="tsr-group">
              <h3 :id="`categoryTitle-${group.module.id}`" class="tsr-group-title">
                {{ group.module.title }} ({{ group.count }} {{ group.count === 1 ? 'Result' : 'Results' }})
              </h3>
              <div class="row g-4">
                <div
                  v-for="lesson in group.lessons"
                  :key="lesson.id"
                  class="col-12 col-md-6 col-lg-4">
                  <TransformLessonCard
                    :lesson="lesson"
                    :index="lesson.displayNumber ?? 1"
                    :thumbnailUrl="thumbnailUrls[lesson.id] ?? null"
                    :hasAssociatedQuiz="lesson.lessonType !== 'video' || group.videoIdsWithQuiz.has(lesson.id)"
                    :lessonIndexMap="group.lessonIndexMap" />
                </div>
              </div>
            </section>
          </template>

      <!-- Empty state -->
      <div v-else-if="hasSearched" class="tsr-empty d-flex flex-column align-items-center justify-content-center text-center">
        <img :src="emptySearchIcon" alt="" class="tsr-empty-icon mb-3" />
        <p class="header-20 mb-2"><strong>No results for "{{ currentTerm }}"</strong></p>
        <p class="text-16 text-gray-700 mb-0">
          We couldn't find any lessons or quizzes that match your search. Try a different term or
          <button type="button" class="btn btn-link p-0 tsr-clear-inline" @click="clearAndReturn">clear your search</button>.
        </p>
      </div>
    </div>

    <Footer></Footer>
  </div>
</template>

<style scoped lang="scss">
  .tsr-title {
    margin-top: 28px; /* 28px + 5px breadcrumb margin-bottom = 33px total */
    margin-bottom: 8px; /* 8px separation between title and subtitle */
    color: var(--Body-Color, #212529);
    font-family: 'DM Sans';
    font-size: 28px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 36.4px */
    letter-spacing: -0.56px;
  }

  .tsr-subtitle {
    max-width: 730px;
    color: var(--Gray-700, #495057);
    font-family: 'DM Sans';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%; /* 20.8px */
    letter-spacing: -0.32px;
  }

  /* 30px between the subtitle (mb-0) and the search bar. */
  .tsr-search {
    max-width: 730px;
    margin-top: 30px;

    /* Define CSS custom properties for search input styling */
    --tsr-search-color: var(--bs-body-color);
    --tsr-search-font: 'Helvetica Neue', -apple-system, sans-serif;
    --tsr-search-font-size: 16px;
    --tsr-search-font-weight: 400;
    --tsr-search-line-height: 150%; /* 24px */

    /* Apply custom properties to search input */
    :deep(.form-control.search-input) {
      color: var(--tsr-search-color);
      font-family: var(--tsr-search-font);
      font-size: var(--tsr-search-font-size);
      font-style: normal;
      font-weight: var(--tsr-search-font-weight);
      line-height: var(--tsr-search-line-height);
    }

    /* Style the placeholder */
    :deep(.form-control.search-input::placeholder) {
      color: var(--tsr-search-color);
      opacity: 0.6;
    }
  }

  /* 50px between the search bar and the results summary;
     33px between the summary and the first category title. */
  .tsr-summary {
    margin-top: 50px;
    margin-bottom: 33px;
  }

  /* Header - 22 - Bold */
  .tsr-summary-count {
    color: var(--bs-body-color); /* #212529 */
    font-family: 'DM Sans';
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%; /* 27.5px */
    letter-spacing: -0.44px;
    word-break: break-word;
  }

  /* 20px gap from the results count. */
  .tsr-clear-link {
    color: var(--bs-primary);
    text-decoration: underline;
    white-space: nowrap;
    flex-shrink: 0;
    font-weight: 500;
    margin-left: 20px;
  }

  .tsr-group {
    margin-bottom: 48px;
  }

  /* Header - 20 - Bold */
  .tsr-group-title {
    color: var(--bs-body-color); /* #212529 */
    font-family: 'DM Sans';
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%; /* 25px */
    letter-spacing: -0.4px;
    margin-bottom: 30px;
  }

  .tsr-empty {
    min-height: 320px;
    padding: 48px 16px;
  }

  .tsr-empty-icon {
    width: 96px;
    height: 96px;
  }

  .tsr-clear-inline {
    color: var(--bs-primary);
    text-decoration: underline;
    font-size: 16px;
    vertical-align: baseline;
  }
</style>
